/**
 * Push-audience evaluation (roadmap Milestone 8, 方案甲).
 *
 * Push is not a new protocol: it is `recommend = back(ActorRoute(subject -> recipient))`, an ownable, appreciating
 * asset. The only thing missing was a way to choose *whom* to push to so the act pays off. This kernel answers that
 * from the locally visible [CommitmentSnapshot] alone — no global ranking, no central recommender (roadmap §4 / §13).
 *
 * The signal is exactly the one the design names: walk a subject post's causal ancestry (parent post → thread context →
 * the author's meaning origin, transparently through any reply [ActorRoute]) and harvest the [Commitment]s on each
 * ancestor. An actor that staked on the subject's neighbourhood is a high-probability backer of the subject itself, and
 * the closer and heavier that stake, the stronger the pull. A bounded capacity term (frame-free issuance, or a relative
 * price when a measuring frame exists) modulates the signal by the actor's expected committing volume.
 *
 * Pure over the snapshot and decoupled from the (heavy, fallible) value-field solve: capacity defaults to issuance
 * computed directly from the snapshot, while [priceOf] is an optional seam for frame-relative modulation. The output is
 * a *suggestion*; whether to spend a commitment on the route stays the actor's decision (human and agent alike).
 */

package com.oasisfeng.invaluable.social

import com.oasisfeng.invaluable.NestedLink
import com.oasisfeng.invaluable.actor.Actor
import com.oasisfeng.invaluable.actor.ActorRoute
import com.oasisfeng.invaluable.actor.CommitmentSnapshot
import com.oasisfeng.invaluable.dsl.Post
import com.oasisfeng.invaluable.dsl.profile
import kotlin.math.ln

/** One ranked push candidate: who to route [subject][AudienceValuation.rank] to, a heuristic [score], and why. */
data class RecipientScore(val recipient: Actor, val score: Double, val reason: String)

object AudienceValuation {

    /** Default number of candidates surfaced; ample for the MVP's <10-node internal test. */
    const val DEFAULT_LIMIT = 5

    /**
     * Rank candidate recipients for pushing [subject], strongest first.
     *
     * @param pusher the actor who would pay for the route (excluded from its own audience).
     * @param priceOf optional frame-relative capacity for an actor; when it returns null the kernel falls back to the
     *   actor's frame-free issuance computed from [snapshot]. Pass `{ null }` (default) to use issuance only.
     */
    fun rank(
        subject: Post,
        snapshot: CommitmentSnapshot,
        pusher: Actor,
        limit: Int = DEFAULT_LIMIT,
        priceOf: (Actor) -> Double? = { null },
    ): List<RecipientScore> {
        // Backers of the subject are already converted; the author and the pusher are not their own push audience.
        val excluded = buildSet {
            add(pusher)
            subject.profile()?.actor()?.let(::add)
            snapshot.commitmentsOf(subject.link).forEach { add(it.issuer) }
        }

        // Accumulate a proximity-weighted backing signal and remember the closest ancestor each actor staked on.
        val signal = mutableMapOf<Actor, Double>()
        val staked = mutableMapOf<Actor, Double>()      // summed |q| on the ancestry, for the reason text
        val closest = mutableMapOf<Actor, Int>()        // nearest ancestor depth (1 = the immediate parent)
        for ((ancestor, depth) in ancestorChain(subject)) {
            val weight = 1.0 / depth
            for (commitment in snapshot.commitmentsOf(ancestor)) {
                val actor = commitment.issuer
                if (actor in excluded) continue
                val amount = commitment.quantity.abs().toDouble()
                signal[actor] = (signal[actor] ?: 0.0) + weight * amount
                staked[actor] = (staked[actor] ?: 0.0) + amount
                closest[actor] = minOf(closest[actor] ?: depth, depth)
            }
        }

        return signal.entries
            .map { (actor, sig) ->
                val capacity = priceOf(actor) ?: issuanceOf(actor, snapshot)
                // Capacity modulates sub-linearly so proximity stays the dominant driver, while a heavier committer
                // (the "expected commitment volume" pull) still outranks a peer with the same proximity signal.
                RecipientScore(actor, sig * (1.0 + ln(1.0 + capacity)), reasonFor(staked.getValue(actor), closest.getValue(actor)))
            }
            .sortedWith(compareByDescending<RecipientScore> { it.score }.thenBy { it.recipient.toString() })
            .take(limit)
    }

    /**
     * Causal-ancestry links of [subject] paired with depth (1 = immediate parent), nearest first.
     *
     * Follows `cause()` upward through nested posts and transparently through reply [ActorRoute]s (continuing from the
     * route's canonical subject), stopping at the author's `actor -> MEANING` origin or any unrecognised cause shape.
     * A visited guard keeps a degenerate chain from looping.
     */
    private fun ancestorChain(subject: Post): List<Pair<NestedLink, Int>> = buildList {
        val seen = mutableSetOf<NestedLink>()
        var depth = 1
        var cause: NestedLink = subject.cause()
        while (seen.add(cause)) {
            if (Actor.render(cause) != null) break          // reached the author's meaning origin: no further backers
            add(cause to depth++)
            val parentPost = Post.render(cause)
            if (parentPost != null) { cause = parentPost.cause(); continue }
            val route = ActorRoute.render(cause)
            if (route != null) { cause = route.subject; continue }
            break                                           // unrecognised cause shape
        }
    }

    /** Frame-free issuance of [actor]: the sum of absolute committed quantities visible in [snapshot]. */
    private fun issuanceOf(actor: Actor, snapshot: CommitmentSnapshot): Double =
        snapshot.commitments.fold(0.0) { sum, c -> if (c.issuer == actor) sum + c.quantity.abs().toDouble() else sum }

    private fun reasonFor(staked: Double, closestDepth: Int): String {
        val where = if (closestDepth <= 1) "the parent post" else "this thread's context"
        return "backed $where (staked ≈ ${formatAmount(staked)})"
    }
}
